/**
 * The back button in Activity — Aaron, Oct 26: "I noticed it in an email on
 * mobile, when I pressed back it took me to the contacts screen. It doesn't
 * seem to be registering anything that's happening in activities as a page
 * change … If I'm clicked into an email and I press back it should take me
 * back to the email list." (`docs/activity.md` §13.3, §13.5.)
 *
 * Everything in Activity used to change the address with `replace`, or not at
 * all, so the whole page was one history entry and back left it for whatever
 * came before. Now each thing somebody experiences as going somewhere is an
 * entry of its own, and back steps out of them one at a time, the topmost
 * first. Two kinds of thing:
 *
 *  - **What the address names** — `?tab=`, `?c=`, `?stop=`, `?task=`,
 *    `?chat=` — through `useActivityNav()`: `push` opens it in a new entry,
 *    `replace` changes this one (a refinement: a day, a view, a filter, a
 *    search — never an entry each, or back becomes a list of every day
 *    somebody swiped past), and `close` takes it out of the address. The
 *    address stays the one source, so a deep link and a reload land on the
 *    same thing.
 *  - **A sheet the address does not name** — a New sheet, the people sheet, a
 *    phone's bottom sheets, the stop page's screens — through
 *    `useBackLayer(onBack)`, called while it is open: an entry of its own at
 *    the same address, spent by back calling `onBack`. A sheet with unsaved
 *    changes asks its own *Close without saving?* and answers `false`; its
 *    entry is put back at once, so it stays open and the next back asks again.
 *    A sheet may also carry address keys (the Task sheet's `?task=`): then it
 *    is both, opened by the address and guarded by its question.
 *
 * **This never navigates by itself** — not on a timer, not on load. Something
 * closed by its own ✕, Cancel or ‹ is not stepped back from (a back a tick
 * later collides with whatever the click went on to do — a job opened, a page
 * loaded); its entry stays where it is and is marked **spent**: the address
 * stops naming what closed, here, and the next thing opened takes the spent
 * entry's place rather than stacking on it. Back, when it is pressed, steps
 * over spent entries — landing on one, it carries on the way it was going —
 * and back from a spent entry that was something over a page steps over that
 * page as well, because what is on screen *is* that page (with whatever was
 * refined while the sheet was up): a spent entry never costs a press.
 *
 * Every entry this page makes says so inside react-router's own state
 * (`usr.rowtedBack`): the keys it opened, the sheet it belongs to, whether it
 * is spent; `history.replaceState` keeps the mark through the refinements
 * other code makes (the list engine's `replace`), because a refinement is the
 * same entry changed, and the marks of the entries under this one are kept
 * here as they are made.
 */
import { useContext, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { UNSAFE_LocationContext, UNSAFE_NavigationContext } from 'react-router-dom'

/** Where an entry says what made it, inside react-router's `usr`. */
export const BACK_MARK = 'rowtedBack'

export interface BackMark {
  /** The address keys this entry opened. */
  keys?: string[]
  /** A sheet's own entry, at the address of the page under it. */
  sheet?: string
  /** What it was opened for has closed: back steps over it. */
  spent?: boolean
  /** The page under it with something over it (not another tab): back from it, spent, steps over that page too. */
  over?: boolean
  /** Keys the page wrote by itself, not a person's choice (the conversation a desktop Inbox opens on its own). */
  auto?: string[]
}

interface RouterState { usr?: unknown; key?: string; idx?: number }

/** `navigate` for a path, as the router's own navigator does it. */
type Go = (path: string, options?: { replace?: boolean; state?: unknown }) => void

/*
 * The router's navigator and location straight from its contexts, rather than
 * `useNavigate`: Activity's sheets are drawn in tests and screens with no
 * router at all, and there the back button has nothing to do — `useNavigate`
 * would throw instead.
 */
function useRouter(): { go: Go | null; location: { pathname: string; search: string; key: string } | null } {
  const navigation = useContext(UNSAFE_NavigationContext)
  const at = useContext(UNSAFE_LocationContext)
  const navigator = navigation?.navigator ?? null
  const go = useMemo<Go | null>(() => (navigator
    ? (path, options) => {
      if (options?.replace) navigator.replace(path, options.state)
      else navigator.push(path, options?.state)
    }
    : null), [navigator])
  return { go, location: at?.location ?? null }
}

interface Sheet {
  token: string
  /** The history index of its entry. */
  idx: number
  /** Its entry's address and state — kept current through refinements, so a kept sheet is put back as it was. */
  url: string
  usr: unknown
  onBack: () => boolean | void
  navigate: Go
  /** Address keys that go with it. */
  keys: readonly string[]
}

const sheets: Sheet[] = []
/** The marks of the entries this page made, by history index — `history.state` only says the current one's. */
const marks = new Map<number, BackMark>()
/**
 * Address keys on their way out: the router draws the address a moment after
 * it changes (a transition), and nothing should show what was closed
 * meanwhile. Each is hidden only in the entry it was closed in, by the
 * router's key, so the same address met again later shows it.
 */
const leaving = new Map<string, { value: string; entry: string }>()
let lastIdx = 0
let version = 0
/** A back this page is carrying on with (stepping over spent entries): whether the page under the first is to be stepped over too. */
let travel: { skipBase: boolean; at: number } | null = null
const watchers = new Set<() => void>()

const notify = (): void => {
  version += 1
  for (const watcher of watchers) watcher()
}

const stateNow = (): RouterState | null => (window.history.state ?? null) as RouterState | null
/** The entry's own key, as react-router names it (`default` for the first, which it never keyed). */
const entryNow = (): string => stateNow()?.key ?? 'default'
const indexNow = (): number => {
  const idx = stateNow()?.idx
  return typeof idx === 'number' ? idx : 0
}
/*
 * Where the page is. Under the app's `BrowserRouter` the window says, and says
 * it before the router has drawn it (a push is a transition); under a
 * `MemoryRouter` — a component test — the window knows nothing, so the router's
 * own location, as the hooks last read it, does.
 */
let routerAt: { pathname: string; search: string } = { pathname: '/', search: '' }
const driven = (): boolean => typeof stateNow()?.idx === 'number'
const at = (): { pathname: string; search: string } => (driven() ? window.location : routerAt)
const here = (): string => `${at().pathname}${at().search}`
const pathOf = (search: URLSearchParams): string => {
  const words = search.toString()
  return `${at().pathname}${words ? `?${words}` : ''}`
}

function markIn(state: RouterState | null | undefined): BackMark | null {
  const usr = state?.usr
  if (!usr || typeof usr !== 'object') return null
  const mark = (usr as Record<string, unknown>)[BACK_MARK]
  return mark && typeof mark === 'object' ? mark as BackMark : null
}

/** The mark of the entry at `idx`: this one's from its state, the ones under it from what this page made. */
function markAt(idx: number): BackMark | null {
  if (idx === indexNow()) return markIn(stateNow()) ?? marks.get(idx) ?? null
  return marks.get(idx) ?? null
}

/** Spent: closed by itself, or a sheet's entry whose sheet has gone (a reload, a push over it). */
function spent(mark: BackMark | null): boolean {
  if (!mark) return false
  if (mark.spent) return true
  return Boolean(mark.sheet && !sheets.some((sheet) => sheet.token === mark.sheet))
}

/** `usr` with the mark set (or taken off), everything else in it kept. */
export function usrWith(usr: unknown, mark: BackMark | null): unknown {
  const base: Record<string, unknown> = usr && typeof usr === 'object' ? { ...(usr as Record<string, unknown>) } : {}
  delete base[BACK_MARK]
  if (mark) base[BACK_MARK] = mark
  return Object.keys(base).length > 0 ? base : null
}

/** Whether `key` is on its way out of the entry the window is on. */
const leavingNow = (key: string, value: string | null): boolean => {
  const record = leaving.get(key)
  return Boolean(record && record.value === value && record.entry === entryNow())
}

/** The address as it is, without what is on its way out. */
function searchNow(): URLSearchParams {
  const search = new URLSearchParams(at().search)
  for (const key of [...search.keys()]) if (leavingNow(key, search.get(key))) search.delete(key)
  return search
}

// ---------------------------------------------------------------------------
// Watching the history
// ---------------------------------------------------------------------------

const INSTALLED = Symbol.for('rowted.back')

function install(): void {
  const history = window.history as History & { [INSTALLED]?: true }
  if (history[INSTALLED]) return
  history[INSTALLED] = true
  const push = history.pushState.bind(history)
  const replace = history.replaceState.bind(history)
  history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
    push(data, unused, url)
    const idx = indexNow()
    /* A push throws away every entry ahead of it, and their marks with them. */
    for (const at of [...marks.keys()]) if (at >= idx) marks.delete(at)
    const mark = markIn(data as RouterState)
    if (mark) marks.set(idx, mark)
    lastIdx = idx
  }
  history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
    const had = markIn(stateNow())
    let next = data as RouterState | null
    /* A refinement is the same entry changed: it keeps what made it (on the same page only). */
    const samePage = url === undefined || url === null
      || new URL(String(url), window.location.href).pathname === window.location.pathname
    if (had && samePage && next && typeof next === 'object' && !markIn(next)) {
      next = { ...next, usr: usrWith(next.usr, had) }
    }
    replace(next, unused, url)
    const idx = indexNow()
    const mark = markIn(next)
    if (mark) marks.set(idx, mark)
    else marks.delete(idx)
    for (const sheet of sheets) {
      if (sheet.idx === idx) { sheet.url = here(); sheet.usr = next?.usr ?? null }
    }
    lastIdx = idx
  }
  /*
   * Capturing, so it runs before react-router's own listener: a sheet that
   * keeps itself open puts its entry back before the router has drawn the
   * page it was leaving for — which matters when that page is another one.
   */
  window.addEventListener('popstate', onPop, true)
  lastIdx = indexNow()
  /* A reload keeps the entry's state: what this one was is known, the ones under it are not. */
  const mark = markIn(stateNow())
  if (mark) marks.set(lastIdx, mark)
}

function onPop(): void {
  const idx = indexNow()
  const from = lastIdx
  const leftMark = marks.get(from) ?? null
  /* Read before any sheet leaves: a sheet closed by this back is not a spent entry left behind. */
  const carrying = travel && Date.now() - travel.at < 1500 ? travel : null
  travel = null
  const skipBase = carrying ? carrying.skipBase : idx < from && spent(leftMark) && Boolean(leftMark?.over)
  lastIdx = idx
  /* The sheets whose entries are now ahead of us, the top one first. */
  const gone = sheets.filter((sheet) => sheet.idx > idx).sort((a, b) => b.idx - a.idx)
  for (const sheet of gone) sheets.splice(sheets.indexOf(sheet), 1)
  const kept = gone.filter((sheet) => sheet.onBack() === false)
  if (kept.length > 0) {
    /* It asked its own question and stays open: its entry goes back, bottom first. */
    for (const sheet of kept.reverse()) {
      sheet.navigate(sheet.url, { state: sheet.usr })
      sheet.idx = indexNow()
      sheets.push(sheet)
    }
    return
  }
  const back = idx < from
  /* A spent entry is stepped over, the way back (or forward) was going. */
  if (spent(markAt(idx))) {
    if (back) travel = { skipBase, at: Date.now() }
    window.history.go(back ? -1 : 1)
    return
  }
  /* Back from a spent entry over this page: what was on screen was this page, so it is stepped over too. */
  if (back && skipBase) window.history.go(-1)
}

if (typeof window !== 'undefined') install()

// ---------------------------------------------------------------------------
// Opening and closing
// ---------------------------------------------------------------------------

/** A push onto a spent entry takes its place instead: one click that closed one thing and opened the next is one entry. */
function goTo(navigate: Go, path: string, mark: BackMark): void {
  if (driven() && spent(markAt(indexNow()))) {
    navigate(path, { replace: true, state: usrWith(null, mark) })
    return
  }
  navigate(path, { state: usrWith(null, mark) })
}

/** This entry is spent: written into its state, so a reload knows too. */
function spendHere(navigate: Go, path: string): void {
  const mark: BackMark = { ...(markIn(stateNow()) ?? {}), spent: true }
  navigate(path, { replace: true, state: usrWith(stateNow()?.usr ?? null, mark) })
}

export type AddressSet = Record<string, string | null>

function pushSet(navigate: Go, set: AddressSet, fresh: boolean): void {
  const before = searchNow()
  const search = fresh ? new URLSearchParams() : new URLSearchParams(before)
  for (const [key, value] of Object.entries(set)) {
    if (value === null) search.delete(key)
    else search.set(key, value)
  }
  const path = pathOf(search)
  if (path === here()) return
  const opened = Object.entries(set).filter(([, value]) => value !== null).map(([key]) => key)
  for (const key of opened) leaving.delete(key)
  /* Over this page: only keys it did not have, onto its own address — not another tab. */
  const over = !fresh && Object.keys(set).every((key) => !before.has(key))
  goTo(navigate, path, { keys: opened, over })
}

function replaceSet(navigate: Go, set: AddressSet, opens: boolean, auto: boolean): void {
  const search = searchNow()
  for (const [key, value] of Object.entries(set)) {
    if (value === null) search.delete(key)
    else search.set(key, value)
  }
  const path = pathOf(search)
  const was = markIn(stateNow())
  const opened = Object.entries(set).filter(([, value]) => value !== null).map(([key]) => key)
  /* A key taken out is no longer what this entry opened; `opens` says the keys set now are. */
  const kept = (list: string[] | undefined) => (list ?? []).filter((key) => !(key in set))
  const keys = was?.keys || (opens && was)
    ? [...kept(was?.keys), ...(opens ? opened : (was?.keys ?? []).filter((key) => key in set && set[key] !== null))]
    : null
  const written = auto ? [...kept(was?.auto), ...opened] : kept(was?.auto)
  const mark: BackMark | null = was || written.length > 0 ? {
    ...(was ?? {}),
    ...(keys ? { keys: [...new Set(keys)] } : {}),
    auto: written,
  } : null
  if (mark && mark.auto?.length === 0) delete mark.auto
  if (path === here() && !opens && !auto) return
  navigate(path, { replace: true, state: usrWith(stateNow()?.usr ?? null, mark) })
}

/**
 * Takes `keys` out of the address, here, and spends the entries this page
 * pushed for them — this one and the ones under it that opened them (a
 * task's under its chat's) — so back steps over them all at once.
 */
function closeKeys(navigate: Go, keys: readonly string[]): void {
  const search = new URLSearchParams(at().search)
  const present = keys.filter((key) => search.has(key) && !leavingNow(key, search.get(key)))
  if (present.length === 0) return
  if (driven()) {
    const remaining = new Set(present)
    for (let idx = indexNow() - 1; idx > 0 && remaining.size > 0; idx -= 1) {
      const mark = marks.get(idx)
      if (!mark) break
      if (mark.sheet) continue
      if (!mark.keys?.some((key) => remaining.has(key))) break
      marks.set(idx, { ...mark, spent: true })
      for (const key of mark.keys) remaining.delete(key)
    }
  }
  for (const key of present) leaving.set(key, { value: search.get(key)!, entry: entryNow() })
  notify()
  const next = searchNow()
  if (driven()) spendHere(navigate, pathOf(next))
  else navigate(pathOf(next), { replace: true, state: stateNow()?.usr ?? null })
}

/**
 * A link that opened something over nothing of ours — a thread on a phone,
 * from the bell or a record — gets the page under it put in first, so back
 * goes to the list rather than out of Activity.
 */
function layerUnder(navigate: Go, keys: readonly string[]): void {
  /* Only the window's history has entries to put under (a test's memory router has no marks to stop at). */
  if (!driven()) return
  const search = searchNow()
  const present = keys.filter((key) => search.has(key))
  if (present.length === 0) return
  const full = pathOf(search)
  const below = new URLSearchParams(search)
  for (const key of present) below.delete(key)
  navigate(pathOf(below), { replace: true, state: stateNow()?.usr ?? null })
  navigate(full, { state: usrWith(null, { keys: present, over: true }) })
}

export interface ActivityNav {
  /** What the address says for `key` — null while it is on its way out. */
  get: (key: string) => string | null
  /**
   * Opens what `set` names in a new entry (`null` takes a key out). `fresh`
   * starts from an empty address — another tab, not this page with more on it.
   */
  push: (set: AddressSet, options?: { fresh?: boolean }) => void
  /**
   * Changes this entry: a refinement. `opens` says the keys set now are what this entry opened;
   * `auto` that the page wrote them by itself (`wroteItself`).
   */
  replace: (set: AddressSet, options?: { opens?: boolean; auto?: boolean }) => void
  /** Takes away what `keys` opened: out of the address here, and back steps over the entries that opened them. */
  close: (keys: readonly string[]) => void
  /** Whether this entry is the one that opened `key` (a link opened it otherwise). */
  opened: (key: string) => boolean
  /** Whether the page wrote `key` into this entry by itself rather than somebody choosing it. */
  wroteItself: (key: string) => boolean
  /** Puts the page without `keys` under this entry — a link that opened them over nothing. */
  layerUnder: (keys: readonly string[]) => void
}

const subscribe = (watcher: () => void): (() => void) => {
  watchers.add(watcher)
  return () => { watchers.delete(watcher) }
}
const snapshot = (): number => version

const OUTSIDE: ActivityNav = {
  get: () => null, push: () => {}, replace: () => {}, close: () => {}, opened: () => false, wroteItself: () => false,
  layerUnder: () => {},
}

export function useActivityNav(): ActivityNav {
  const { go: navigate, location } = useRouter()
  const pathname = location?.pathname ?? '/'
  const search = location?.search ?? ''
  const entry = location?.key ?? 'default'
  if (location) routerAt = { pathname, search }
  const seen = useSyncExternalStore(subscribe, snapshot, snapshot)
  /* What was on its way out has gone once the page draws another entry. */
  useEffect(() => {
    let changed = false
    for (const [key, record] of [...leaving]) {
      if (record.entry !== entry) { leaving.delete(key); changed = true }
    }
    if (changed) notify()
  }, [entry])
  return useMemo<ActivityNav>(() => {
    const params = new URLSearchParams(search)
    void seen
    if (!navigate) return OUTSIDE
    return {
      get: (key) => {
        const value = params.get(key)
        const record = leaving.get(key)
        return value !== null && record?.value === value && record.entry === entry ? null : value
      },
      push: (set, options) => pushSet(navigate, set, options?.fresh ?? false),
      replace: (set, options) => replaceSet(navigate, set, options?.opens ?? false, options?.auto ?? false),
      close: (keys) => closeKeys(navigate, keys),
      opened: (key) => Boolean(markAt(indexNow())?.keys?.includes(key)),
      wroteItself: (key) => Boolean(markAt(indexNow())?.auto?.includes(key)),
      layerUnder: (keys) => layerUnder(navigate, keys),
    }
  }, [navigate, search, entry, seen])
}

// ---------------------------------------------------------------------------
// Sheets
// ---------------------------------------------------------------------------

let nextToken = 1

function openSheet(
  navigate: Go, onBack: () => boolean | void, params: Record<string, string> | null, closes: readonly string[],
): Sheet {
  const token = `s${Date.now().toString(36)}${(nextToken++).toString(36)}`
  const search = searchNow()
  const keys = [...Object.keys(params ?? {}), ...closes]
  /* A link (or a step forward) that already names it: it takes the entry it is in. */
  const adopt = params !== null && Object.entries(params).every(([key, value]) => search.get(key) === value)
  if (!adopt) {
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        search.set(key, value)
        leaving.delete(key)
      }
    }
    goTo(navigate, pathOf(search), params ? { keys: Object.keys(params), over: true } : { sheet: token, over: true })
  }
  const sheet: Sheet = { token, idx: indexNow(), url: here(), usr: stateNow()?.usr ?? null, onBack, navigate, keys }
  sheets.push(sheet)
  return sheet
}

function closeSheet(sheet: Sheet): void {
  const index = sheets.indexOf(sheet)
  /* Spent by back already. */
  if (index < 0) return
  sheets.splice(index, 1)
  if (sheet.keys.length > 0) {
    closeKeys(sheet.navigate, sheet.keys)
    return
  }
  /* Its entry is spent (a sheet's entry with no sheet is); written into it while it is the one shown. */
  if (driven() && indexNow() === sheet.idx && markIn(stateNow())?.sheet === sheet.token) spendHere(sheet.navigate, here())
}

/**
 * An entry of its own for as long as this is mounted (and `enabled`): back
 * calls `onBack`, which closes the sheet — or asks its own question and
 * answers `false`, and the entry is put back. Closed any other way, its entry
 * is spent, and back steps over it.
 *
 * `params` are address keys the sheet is named by (`{ task: id }`): opened by
 * a link they are adopted rather than pushed again; `closes` are keys that go
 * with it when it closes (a task's `chat`).
 */
export function useBackLayer(
  onBack: () => boolean | void,
  options: { enabled?: boolean; params?: Record<string, string>; closes?: readonly string[] } = {},
): void {
  const { go: navigate, location } = useRouter()
  if (location) routerAt = { pathname: location.pathname, search: location.search }
  const latest = useRef(onBack)
  latest.current = onBack
  const enabled = options.enabled ?? true
  const params = options.params ?? null
  const named = params ? new URLSearchParams(params).toString() : ''
  const closes = (options.closes ?? []).join(',')
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const routed = navigate !== null
  useEffect(() => {
    if (!enabled || !navigateRef.current) return undefined
    const sheet = openSheet(navigateRef.current, () => latest.current(), named ? Object.fromEntries(new URLSearchParams(named)) : null,
      closes ? closes.split(',') : [])
    return () => closeSheet(sheet)
  }, [enabled, named, closes, routed])
}

/** `useBackLayer` as an element, for a sheet drawn by a component this page does not own (`PanelSheet`). */
export function BackLayer({ onBack }: { onBack: () => boolean | void }): null {
  useBackLayer(onBack)
  return null
}

/** For tests: what this page knows, and a clean slate between them. */
export const backForTests = {
  sheets: (): number => sheets.length,
  leaving: (): string[] => [...leaving.keys()],
  reset: (): void => {
    sheets.length = 0
    marks.clear()
    leaving.clear()
    lastIdx = indexNow()
    travel = null
    notify()
  },
}
